import { AppDispatch } from "utils/redux/store";
import { ChatMessage, LoginlessConversationCache } from "./types";
import {
  setCompletionData,
  setLoginlessConversationCache,
  setLoginlessMessageLimitReached,
  setUIState,
} from "./slice";
import { setChatbotMessages, setThreadId } from "../Chatbot/slice";
import { getLoginlessConversation } from "./weeklyCheckin.api";
import { setConfigCatFlag } from "app/containers/Global/slice";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import { User, type IConfigCatClient } from "configcat-react";
import { IS_AU_INSTANCE } from "utils/environmentVariables";

export const saveCachedLoginlessConversation = (
  dispatch: AppDispatch,
  cachedConversation: LoginlessConversationCache
) => {
  // Restore completion data if available
  if (cachedConversation.isCompleted) {
    dispatch(
      setCompletionData({
        completedAt: cachedConversation.completedAt,
        nextCheckInAvailable: cachedConversation.nextCheckInAvailable,
      })
    );
  }

  // Check message count and set limit state
  saveCachedConversationMessages(cachedConversation, dispatch);

  if (cachedConversation.threadId) {
    dispatch(setThreadId(cachedConversation.threadId));
  }

  if (cachedConversation.messages && cachedConversation.messages.length > 0) {
    dispatch(setChatbotMessages(cachedConversation.messages));
  }
};

const saveCachedConversationMessages = (
  cachedConversation: LoginlessConversationCache,
  dispatch: AppDispatch
) => {
  if (!cachedConversation.messages) {
    dispatch(setUIState(cachedConversation.uiState));
    return;
  }

  const userMessageCount = countUserMessages(cachedConversation.messages);
  if (userMessageCount >= 10 || cachedConversation.uiState === "messageLimitReached") {
    // Keep showing the conversation with input disabled; user can click
    // "Go to Develop" themselves rather than being force-redirected.
    dispatch(setUIState("messageLimitReached"));
    dispatch(setLoginlessMessageLimitReached(true));
    return;
  }

  // Restore UI state from cache
  dispatch(setUIState(cachedConversation.uiState));
  dispatch(setLoginlessMessageLimitReached(false));
};

/**
 * Initialize ConfigCat feature flags for loginless users.
 * Uses companyAccountId from the API response to evaluate targeting rules.
 */
async function initLoginlessConfigCat(
  configCatClient: IConfigCatClient,
  dispatch: AppDispatch,
  userAccountId: number,
  companyAccountId?: number | null
) {
  const subdomainLocaleInstancePrefix = IS_AU_INSTANCE ? "AU" : "US";

  const configCatUser = new User(String(userAccountId), undefined, undefined, {
    companyAccountId: companyAccountId?.toString() ?? "",
    companyAccountIdWithLocale: `${subdomainLocaleInstancePrefix}${
      companyAccountId ?? ""
    }`,
    instance: subdomainLocaleInstancePrefix,
  });

  const flags = Object.values(CONFIG_CAT_FLAGS).map((flag) => ({
    flagName: flag.rawFlagName,
    key: flag.savedFlagName,
    defaultValue: flag.defaultValue,
  }));

  await Promise.all(
    flags.map(async (flag) => {
      const value = await configCatClient.getValueAsync(
        flag.flagName,
        flag.defaultValue,
        configCatUser
      );
      dispatch(setConfigCatFlag({ key: flag.key, value: value as boolean }));
    })
  );
}

export const loadLoginlessConversation = async (
  dispatch: AppDispatch,
  encryptedThreadId?: string,
  configCatClient?: IConfigCatClient
) => {
  if (!encryptedThreadId || encryptedThreadId === "expired") {
    dispatch(setUIState("tokenExpired"));
    return;
  } else if (encryptedThreadId === "completed") {
    dispatch(setUIState("checkInCompleted"));
    return;
  } else if (encryptedThreadId === "limit-reached") {
    dispatch(setUIState("messageLimitReached"));
    return;
  }

  dispatch(setUIState("loading"));

  try {
    const response = await getLoginlessConversation(encryptedThreadId);

    if (!(response.success && response.data)) {
      const newState = getUIStateFromErrorCode(response.errorCode);

      // Show the appropriate StatusView in place; the user can click
      // "Back to Develop by Criteria" themselves if they want to continue
      // in the authenticated app. Cache the threadId (if the error response
      // included one) so that button can still deep-link correctly.
      if (response.threadId) {
        dispatch(
          setLoginlessConversationCache({
            encryptedThreadId,
            threadId: response.threadId,
            messages: [],
            uiState: newState,
          })
        );
      }

      if (newState === "messageLimitReached") {
        // No message data on this error path, so ChatbotContent's own
        // message-count check won't catch it — set the flag explicitly.
        dispatch(setLoginlessMessageLimitReached(true));
      }

      dispatch(setUIState(newState));
      return;
    }

    const {
      threadId,
      userAccountId,
      companyAccountId,
      messages: apiMessages,
      messageCount,
      maxMessages,
      isCompleted,
      completedAt,
      nextCheckInAvailable,
    } = response.data;

    // Initialize ConfigCat with the loginless user's company context
    // so isUsingAIService() can evaluate correctly for message routing
    if (configCatClient && userAccountId) {
      await initLoginlessConfigCat(
        configCatClient,
        dispatch,
        userAccountId,
        companyAccountId
      );
    }

    // Check if the check-in is completed
    if (isCompleted) {
      dispatch(setUIState("checkInCompleted"));
      dispatch(
        setCompletionData({
          completedAt,
          nextCheckInAvailable,
        })
      );

      // Cache completed state data in Redux
      dispatch(
        setLoginlessConversationCache({
          encryptedThreadId,
          threadId: threadId || "",
          userAccountId,
          companyAccountId,
          messages: [],
          uiState: "checkInCompleted",
          messageCount,
          maxMessages,
          isCompleted,
          completedAt,
          nextCheckInAvailable,
        })
      );
      return;
    }

    // Set the threadId in Redux store for the chatbot to use
    if (threadId) {
      dispatch(setThreadId(threadId));
    }

    // Convert API messages to chatbot format and set in Redux
    const chatbotMessages =
      apiMessages?.map((msg) => ({
        role: msg.role,
        content: msg.content,
      })) || [];

    if (chatbotMessages.length > 0) {
      dispatch(setChatbotMessages(chatbotMessages));
    }

    // Check if message limit is reached
    const userMessageCount = countUserMessages(chatbotMessages);
    if (userMessageCount >= 10) {
      // Cache the conversation data and show the message-limit StatusView
      // in place; the user can click through to the authenticated app
      // themselves rather than being force-redirected.
      dispatch(
        setLoginlessConversationCache({
          encryptedThreadId,
          threadId,
          userAccountId,
          companyAccountId,
          messages: chatbotMessages,
          uiState: "messageLimitReached",
          messageCount,
          maxMessages,
        })
      );
      dispatch(setUIState("messageLimitReached"));
      return;
    }

    dispatch(setUIState("active"));
    dispatch(setLoginlessMessageLimitReached(false));

    // Cache API response data in Redux
    dispatch(
      setLoginlessConversationCache({
        encryptedThreadId,
        threadId,
        userAccountId,
        companyAccountId,
        messages: chatbotMessages,
        uiState: "active",
        messageCount,
        maxMessages,
      })
    );
  } catch (err) {
    dispatch(setUIState("tokenExpired"));
  }
};

/**
 * Helper function to generate URL for redirecting to authenticated app
 * @param threadId - Optional thread ID for deep linking to conversation
 * @returns URL string with or without threadId parameter
 */
export const getAuthenticatedAppUrl = (threadId?: string | null): string => {
  return threadId ? `/?openChat=true&threadId=${threadId}` : "/";
};

// Helper function to count user messages
export const countUserMessages = (messages: ChatMessage[] | any[]): number =>
  messages.filter((msg) => msg.role === "user").length;

/**
 * Helper function to determine UI state based on API error codes
 * @param errorCode - The error code from the API response
 * @returns The appropriate UI state
 */
export function getUIStateFromErrorCode(
  errorCode?: string
):
  | "tokenExpired"
  | "invalidToken"
  | "serverError"
  | "checkInCompleted"
  | "messageLimitReached"
  | "active" {
  switch (errorCode) {
    case "TOKEN_EXPIRED":
      return "tokenExpired";
    case "INVALID_TOKEN":
      return "invalidToken";
    case "SERVER_ERROR":
      return "serverError";
    case "CHECKIN_COMPLETED":
      return "checkInCompleted";
    case "MESSAGE_LIMIT_REACHED":
      return "messageLimitReached";
    default:
      return "active";
  }
}
